import { FormGroup, FormHelperText, FormLabel, MenuItem, Stack } from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';

import SelectGroups from 'app/iCalendar/filters/SelectGroups';
import RoundedSelect from 'common/components/RoundedSelect';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { getGroupAction, getGroups } from 'redux/slices/groupSlice';
import { getServicesSavePending } from 'redux/slices/iCalendarV4';
import ServiceImage from '../components/ServiceImage';

import RenderFormFields from 'common/features/renderForms/RenderFormFields';
import SharingParams from 'common/features/SharingParams';
import { getFormConfig } from 'redux/slices/formSlice';
import { getServiceTypes } from 'redux/slices/iCalendarV4';
import ServicePeriodOfService from './ServicePeriodOfService';

const ServiceFormSettings = (props) => {
    const { setStep, step, service, fields, handleFieldChange, cancel, isValidForm, setSelectedCalendars } = props;
    const { classes: flexBox, cx } = useFlexBoxStyles();

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const formConfig = useSelector(getFormConfig);
    const dispatch = useDispatch();
    const timeZone = useSettingValue(SettingsKey.TIMEZONE) || 'Europe/Madrid';
    const savePending = useSelector(getServicesSavePending);
    const user = useSelector(getUser);
    const enableGroups =
        useSettingValue(SettingsKey.ENABLE_USER_GROUPS) && (!user?.userGroupId || user?.managedGroups?.length > 1);
    const [initialGroup, setInitialGroup] = useState();
    const serviceTypes = useSelector(getServiceTypes);
    const userGroups = useSelector(getGroups);

    useEffect(() => {
        if (service?.userGroupId) {
            dispatch(getGroupAction({ id: service?.userGroupId, onSuccess: (data) => setInitialGroup(data) }));
        }
    }, [dispatch, service]);

    const onSetGroup = (g) => {
        const extra = { ...fields.extraFields, ...g?.extraFields };
        handleFieldChange({
            target: {
                name: 'extraFields',
                value: Object.keys(extra).length ? extra : null,
            },
        });
        const facet = userGroups?.find((g) => g.id === fields.userGroupId)?.facet;
        handleFieldChange({
            target: {
                name: 'taxonomies',
                value: [
                    ...(fields.taxonomies?.filter((t) => !t.startsWith('/user-group/')) || []),
                    facet ?? null,
                ].filter(Boolean),
            },
        });
    };

    const onGroupChange = (id) => {
        handleFieldChange({
            target: {
                name: 'userGroupId',
                value: id,
            },
        });
        setSelectedCalendars([]);
        dispatch(getGroupAction({ id, onSuccess: (data) => onSetGroup(data) }));
    };

    const handleNextStep = () => {
        if (isValidForm()) {
            setStep(step + 1);
        } else {
            enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
        }
    };

    const updateField = useCallback(
        (value) => {
            handleFieldChange({
                target: {
                    name: 'sharingParams',
                    value,
                },
            });
        },
        [handleFieldChange],
    );

    return (
        <ValidatorForm
            onSubmit={handleNextStep}
            onError={(e) => {
                enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
            }}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
        >
            <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}>
                <Stack direction="column" spacing={3}>
                    <FormGroup>
                        <FormLabel>{t('services.form.title')}</FormLabel>
                        <CustomTextValidator
                            required
                            name="name"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            autoComplete="new-name"
                            value={fields.name}
                            onChange={handleFieldChange}
                            useRegExpText={false}
                            validators={['maxStringLength: 100']}
                            errorMessages={[t('services.form.titleMaxLength')]}
                        />
                        {!fields?.name ? <FormHelperText error>{t('app.common.required')}</FormHelperText> : null}
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('services.form.periodOfService')}</FormLabel>

                        <ServicePeriodOfService
                            fields={fields}
                            handleFieldChange={handleFieldChange}
                            timeZone={timeZone}
                        />
                    </FormGroup>

                    {enableGroups ? (
                        <FormGroup>
                            <FormLabel>{t('users.group')}</FormLabel>
                            <SelectGroups
                                fullWidth={true}
                                size="big"
                                id={fields.userGroupId || 'all'}
                                onSubmit={(e) => {
                                    onGroupChange(e.target.value);
                                }}
                                disabled={service?.id}
                                selectedById={initialGroup}
                            />
                        </FormGroup>
                    ) : null}

                    <FormGroup>
                        <FormLabel>{t('service.label.templateType')}</FormLabel>
                        <RoundedSelect
                            bold={true}
                            size="big"
                            fullWidth={true}
                            name="serviceType"
                            value={fields.serviceType}
                            onChange={handleFieldChange}
                        >
                            {serviceTypes.map((type) => (
                                <MenuItem key={type.id} value={type.slug}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </RoundedSelect>
                        {!fields?.serviceType ? (
                            <FormHelperText error>{t('app.common.required')}</FormHelperText>
                        ) : null}
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('services.form.image')}</FormLabel>

                        <ServiceImage image={fields?.mainImage} handleFieldChange={handleFieldChange} />
                        {!fields?.mainImage ? <FormHelperText error>{t('app.common.required')}</FormHelperText> : null}
                    </FormGroup>

                    {formConfig ? (
                        <RenderFormFields
                            formConfig={formConfig}
                            fields={fields}
                            handleFieldChange={handleFieldChange}
                        />
                    ) : null}

                    <FormGroup>
                        <SharingParams sharingParams={fields.sharingParams} handleChange={updateField} />
                    </FormGroup>
                </Stack>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth disabled={savePending}>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    type="submit"
                    disabled={savePending}
                    loading={savePending}
                >
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default ServiceFormSettings;
